import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Mail, Phone, RadioTower, X, XCircle, ZoomIn } from 'lucide-react';
import type { ExterneFreimeldung, ReportLanguage } from '../../types';
import { istZurueckweisung } from '../../services/freimeldungLogic';

/**
 * Der Block „Online-Freimeldung" IN der Mangelkarte
 * (docs/freimeldung-online-plan.md §F).
 *
 * Eine Meldung von aussen SCHLIESST DEN MANGEL NICHT. Sie ist eine Behauptung
 * einer Firma, die der Koordinator prueft: „Bestätigen" fuehrt deshalb in den
 * gewohnten Freimelde-Dialog (Datum, Kommentar, Beweisbild sind vorbelegt) und
 * erst dessen „Abschließen" schliesst den Mangel — und bucht danach die
 * Meldung als bestaetigt. „Ablehnen" laesst den Mangel offen und verlangt
 * einen Grund.
 *
 * Bei einem bereits erledigten Mangel gibt es nichts mehr zu schliessen. Dann
 * steht hier nur „Zur Kenntnis genommen": das bucht die Meldung direkt weg,
 * OHNE den Freimelde-Dialog zu oeffnen — der wuerde einen geschlossenen Mangel
 * ein zweites Mal schliessen, mit neuem Datum und neuem Kommentar.
 *
 * ZURUECKWEISUNG (`art = 'zurueckgewiesen'`, Betreiber 28.10.2026): die Firma
 * bestreitet den Mangel mit Begruendung. Das ist NUR EIN HINWEIS. Am offenen
 * Mangel stehen dann „Mangel schließen" (derselbe Freimelde-Dialog wie beim
 * Bestaetigen, die Begruendung steht im Kommentar) und „Zur Kenntnis — bleibt
 * offen" (bucht die Meldung als abgelehnt, der Mangel bleibt, wie er ist).
 */

/** Der Vermerk, mit dem „Zur Kenntnis — bleibt offen" die Zurueckweisung bucht. */
const KENNTNIS_ZURUECKWEISUNG = {
    de: 'Zurückweisung zur Kenntnis genommen. Mangel bleibt offen.',
    en: 'Rejection acknowledged. Defect remains open.',
};

interface Props {
    freimeldungen: ExterneFreimeldung[];
    /** Ist der Mangel bereits erledigt? Dann nur „Zur Kenntnis genommen". */
    itemClosed: boolean;
    /**
     * Klartext der letzten gescheiterten Entscheidung (`entscheidungsFehler`
     * aus useFreimeldungen). Er steht HIER, weil hier geklickt wurde: das
     * Panel ist standardmaessig zugeklappt, und ohne diese Zeile sah der
     * Koordinator nur, dass nichts passiert — nicht, warum.
     */
    fehler?: string | null;
    language?: ReportLanguage;
    /** Lesemodus (Nur-Ansicht oder geschlossener Mangel): keine Knoepfe. */
    readOnly?: boolean;
    /** Oeffnet den Freimelde-Dialog mit den Werten der Meldung. */
    onBestaetigen?: (f: ExterneFreimeldung) => void;
    onAblehnen?: (f: ExterneFreimeldung, kommentar: string) => void;
    /** Bucht die Meldung ohne Freimelde-Dialog weg (Mangel ist schon zu). */
    onKenntnis?: (f: ExterneFreimeldung) => void;
}

const datumText = (iso: string | null | undefined): string => {
    const roh = (iso || '').trim();
    if (!roh) return '';
    const ms = Date.parse(roh);
    if (Number.isNaN(ms)) return roh;
    return new Date(ms).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

export const FreimeldungHinweis: React.FC<Props> = ({
    freimeldungen,
    itemClosed,
    fehler = null,
    language = 'de',
    readOnly = false,
    onBestaetigen,
    onAblehnen,
    onKenntnis,
}) => {
    const en = language === 'en';
    // Eigener Zoom-Zustand wie bei den Mangelfotos der Karte: Klick auf das
    // Vorschaubild legt das Bild gross ueber die Seite.
    const [zoom, setZoom] = useState<string | null>(null);
    const [ablehnenId, setAblehnenId] = useState<string | null>(null);
    const [ablehnenText, setAblehnenText] = useState('');

    if (!freimeldungen || freimeldungen.length === 0) return null;

    const ablehnenBestaetigen = (f: ExterneFreimeldung) => {
        onAblehnen?.(f, ablehnenText);
        setAblehnenId(null);
        setAblehnenText('');
    };

    return (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 border-b border-amber-200 pb-2">
                <RadioTower className="w-4 h-4 text-amber-600" />
                <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider">
                    {en ? 'Online clearance report' : 'Online-Freimeldung'} ({freimeldungen.length})
                </h4>
            </div>

            {freimeldungen.map((f) => {
                const zurueck = istZurueckweisung(f);
                return (
                <div key={f.id} className={`bg-white border rounded-lg p-3 space-y-2 ${zurueck ? 'border-rose-200' : 'border-amber-100'}`}>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                        {zurueck && (
                            <span className="self-center px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider">
                                {en ? 'Rejected' : 'Zurückgewiesen'}
                            </span>
                        )}
                        <span className="font-bold text-slate-800">{f.firma || (en ? 'unknown company' : 'unbekannte Firma')}</span>
                        {f.name && <span className="text-slate-600">· {f.name}</span>}
                        {f.gemeldetAt && <span className="text-[11px] text-slate-400">· {datumText(f.gemeldetAt)}</span>}
                    </div>

                    {f.kommentar && (
                        <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded px-2 py-1.5 border border-slate-100">
                            {zurueck && <span className="font-semibold">{en ? 'Reason: ' : 'Begründung: '}</span>}
                            {f.kommentar}
                        </p>
                    )}

                    <div className="flex flex-wrap items-start gap-3">
                        {f.fotoUrl && (
                            <div
                                className="relative h-24 w-24 shrink-0 rounded-lg overflow-hidden border border-amber-200 cursor-zoom-in group/frei"
                                onClick={() => setZoom(f.fotoUrl)}
                                title={en ? 'Click to enlarge' : 'Klicken zum Vergrößern'}
                            >
                                <img src={f.fotoUrl} className="w-full h-full object-cover" alt={en ? 'Proof photo' : 'Beweisfoto'} />
                                <div className="absolute inset-0 bg-black/0 group-hover/frei:bg-black/10 flex items-center justify-center transition-colors">
                                    <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover/frei:opacity-100 drop-shadow-md" />
                                </div>
                            </div>
                        )}
                        {(f.email || f.phone) && (
                            <div className="text-[11px] text-slate-500 space-y-1">
                                {f.email && (
                                    <a href={`mailto:${f.email}`} className="flex items-center gap-1 hover:text-blue-600 underline decoration-dotted">
                                        <Mail className="w-3 h-3" /> {f.email}
                                    </a>
                                )}
                                {f.phone && (
                                    <a href={`tel:${f.phone}`} className="flex items-center gap-1 hover:text-blue-600 underline decoration-dotted">
                                        <Phone className="w-3 h-3" /> {f.phone}
                                    </a>
                                )}
                            </div>
                        )}
                    </div>

                    {!readOnly && (
                        <div className="pt-1">
                            {itemClosed ? (
                                <button
                                    type="button"
                                    onClick={() => onKenntnis?.(f)}
                                    className="relative treffer-44 px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 text-[11px] font-bold uppercase tracking-wider hover:bg-amber-100 transition-colors flex items-center gap-1.5"
                                >
                                    <CheckCircle className="w-3.5 h-3.5" /> {en ? 'Acknowledged' : 'Zur Kenntnis genommen'}
                                </button>
                            ) : zurueck ? (
                                <div className="flex flex-wrap gap-3">
                                    <button
                                        type="button"
                                        onClick={() => onBestaetigen?.(f)}
                                        className="relative treffer-44 px-3 py-1.5 rounded-lg bg-green-600 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-green-700 transition-colors flex items-center gap-1.5"
                                    >
                                        <CheckCircle className="w-3.5 h-3.5" /> {en ? 'Close defect' : 'Mangel schließen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onAblehnen?.(f, en ? KENNTNIS_ZURUECKWEISUNG.en : KENNTNIS_ZURUECKWEISUNG.de)}
                                        className="relative treffer-44 px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 text-[11px] font-bold uppercase tracking-wider hover:bg-amber-100 transition-colors flex items-center gap-1.5"
                                    >
                                        <CheckCircle className="w-3.5 h-3.5" /> {en ? 'Acknowledge (stays open)' : 'Zur Kenntnis (bleibt offen)'}
                                    </button>
                                </div>
                            ) : ablehnenId === f.id ? (
                                <div className="space-y-3">
                                    <textarea
                                        rows={2}
                                        autoFocus
                                        value={ablehnenText}
                                        onChange={(e) => setAblehnenText(e.target.value)}
                                        placeholder={en ? 'Why is the defect not cleared?' : 'Warum ist der Mangel nicht behoben?'}
                                        className="w-full px-3 py-2 rounded-lg border border-rose-200 bg-white text-sm focus:ring-2 focus:ring-rose-200 outline-none"
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => ablehnenBestaetigen(f)}
                                            className="relative treffer-44 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-rose-700 transition-colors"
                                        >
                                            {en ? 'Reject' : 'Ablehnen'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setAblehnenId(null); setAblehnenText(''); }}
                                            className="relative treffer-44 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 text-[11px] font-bold uppercase tracking-wider hover:bg-slate-50 transition-colors"
                                        >
                                            {en ? 'Cancel' : 'Abbrechen'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-3">
                                    <button
                                        type="button"
                                        onClick={() => onBestaetigen?.(f)}
                                        className="relative treffer-44 px-3 py-1.5 rounded-lg bg-green-600 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-green-700 transition-colors flex items-center gap-1.5"
                                    >
                                        <CheckCircle className="w-3.5 h-3.5" /> {en ? 'Confirm' : 'Bestätigen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setAblehnenId(f.id); setAblehnenText(''); }}
                                        className="relative treffer-44 px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-600 text-[11px] font-bold uppercase tracking-wider hover:bg-rose-50 transition-colors flex items-center gap-1.5"
                                    >
                                        <XCircle className="w-3.5 h-3.5" /> {en ? 'Reject' : 'Ablehnen'}
                                    </button>
                                </div>
                            )}
                            {/* Unter den Knoepfen, nicht ueber der Liste: der
                                Satz gehoert zu dem Klick, der eben ins Leere
                                lief. Steht an jeder offenen Meldung, weil der
                                Hook nur EINEN letzten Fehler kennt. */}
                            {fehler && (
                                <p className="mt-2 flex items-start gap-1.5 text-[11px] font-medium text-rose-700">
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                                    <span>
                                        {en ? 'Decision notice: ' : 'Hinweis zur Entscheidung: '}
                                        {fehler}
                                    </span>
                                </p>
                            )}
                        </div>
                    )}
                </div>
                );
            })}

            {zoom && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200 p-4"
                    onClick={() => setZoom(null)}
                >
                    <div className="relative max-w-4xl max-h-[90vh] bg-transparent flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                        <img src={zoom} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl border-4 border-amber-400" alt={en ? 'Proof photo' : 'Beweisfoto'} />
                        <button
                            type="button"
                            onClick={() => setZoom(null)}
                            className="absolute -top-4 -right-4 bg-white text-slate-800 p-2 rounded-full shadow-lg hover:bg-slate-100 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
